import React, { useState } from 'react';
import { sendVerificationCode, confirmVerificationCode } from '../../services/auth';
import { useResendCountdown } from '../../hooks/useResendCountdown';
import { mapVerificationError } from '../../utils/verification';

const COPY = {
  en: {
    pendingTitle: 'Email verification pending',
    pendingHint:
      'Your email is not verified yet. Verify it to sign in with email and recover your password.',
    sendCode: 'Send verification code',
    sending: 'Sending…',
    codeLabel: 'Verification code',
    codePlaceholder: '6-digit code',
    codeError: 'Please enter a 6-digit verification code.',
    confirm: 'Verify email',
    confirming: 'Verifying…',
    resend: 'Resend code',
    resendWait: 'Retry in {n}s',
    success: 'Email verified successfully.',
    sentTo: 'Code sent to {email}',
  },
  zh: {
    pendingTitle: '邮箱待验证',
    pendingHint: '邮箱未验证，完成验证后可使用邮箱登录与找回密码。',
    sendCode: '发送验证码',
    sending: '发送中…',
    codeLabel: '验证码',
    codePlaceholder: '6 位数字验证码',
    codeError: '请输入 6 位数字验证码。',
    confirm: '验证邮箱',
    confirming: '验证中…',
    resend: '重新发送',
    resendWait: '请在 {n}s 后重试',
    success: '邮箱验证成功。',
    sentTo: '验证码已发送至 {email}',
  },
};

function EmailVerificationPanel({
  email,
  purpose,
  lang,
  onSuccess,
  newPassword,
  autoSend = false,
}) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  const [codeSent, setCodeSent] = useState(autoSend);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { secondsLeft, canResend, start } = useResendCountdown(0);

  const handleSend = async () => {
    setActionError('');
    setSuccessMessage('');
    setSending(true);
    try {
      const result = await sendVerificationCode({ email, purpose });
      setCodeSent(true);
      start(result.resendAfterSeconds ?? 60);
      setSuccessMessage(t.sentTo.replace('{email}', email));
    } catch (err) {
      setActionError(mapVerificationError(err?.message, lang));
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setActionError('');
    setSuccessMessage('');
    setCodeError('');

    const trimmedCode = code.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setCodeError(t.codeError);
      return;
    }

    setConfirming(true);
    try {
      const payload = { email, purpose, code: trimmedCode };
      if (newPassword) {
        payload.newPassword = newPassword;
      }
      const result = await confirmVerificationCode(payload);
      if (result.verified || result.reset) {
        setSuccessMessage(t.success);
        if (typeof onSuccess === 'function') {
          onSuccess(result);
        }
      }
    } catch (err) {
      setActionError(mapVerificationError(err?.message, lang));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="email-verification-panel">
      <div className="email-verification-status">
        <p className="email-verification-title">{t.pendingTitle}</p>
        <p className="email-verification-hint">{t.pendingHint}</p>
        <p className="email-verification-email">{email}</p>
      </div>

      {actionError ? <p className="form-error">{actionError}</p> : null}
      {successMessage ? <p className="email-verification-success">{successMessage}</p> : null}

      {!codeSent ? (
        <div className="form-actions email-verification-actions">
          <button type="button" onClick={handleSend} disabled={sending}>
            {sending ? t.sending : t.sendCode}
          </button>
        </div>
      ) : (
        <form onSubmit={handleConfirm}>
          <label className="profile-field">
            <span className="profile-field-label">{t.codeLabel}</span>
            <input
              value={code}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(next);
                if (codeError) setCodeError('');
                if (actionError) setActionError('');
              }}
              placeholder={t.codePlaceholder}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="\d{6}"
            />
          </label>
          {codeError ? <p className="form-error">{codeError}</p> : null}

          <div className="email-verification-resend-row">
            <button
              type="button"
              className="btn-link"
              onClick={handleSend}
              disabled={!canResend || sending}
            >
              {canResend ? t.resend : t.resendWait.replace('{n}', String(secondsLeft))}
            </button>
          </div>

          <div className="form-actions email-verification-actions">
            <button type="submit" disabled={confirming || code.length !== 6}>
              {confirming ? t.confirming : t.confirm}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default EmailVerificationPanel;
